import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Link,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Edit,
  Delete,
  AttachFile,
  AccessTime,
  Link as LinkIcon,
} from '@mui/icons-material';
import { TrainingContentDto } from '../../../api/types/dtos/training';
import SequenceControls from './SequenceControls';
import { canMoveUp, canMoveDown } from '../utils/sequenceHelpers';

interface ContentCardProps {
  content: TrainingContentDto;
  allContents: TrainingContentDto[];
  onEdit: (content: TrainingContentDto) => void;
  onDelete: (contentId: string) => void;
  onMoveUp: (content: TrainingContentDto) => void;
  onMoveDown: (content: TrainingContentDto) => void;
  disabled?: boolean;
}

const ContentCard: React.FC<ContentCardProps> = ({
  content,
  allContents,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  disabled = false,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDelete = () => {
    onDelete(content.id);
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <Card
        sx={{
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            boxShadow: 2,
          },
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6" component="div">
                  {content.name}
                </Typography>
                <Chip
                  label={`Secuencia ${content.sequence}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`${content.progressContribution}% de progreso`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              </Box>

              {content.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {content.description}
                </Typography>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {content.url && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinkIcon fontSize="small" color="action" />
                    <Link
                      href={content.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                    >
                      {content.url}
                    </Link>
                  </Box>
                )}

                {content.durationMinutes && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Duración: {content.durationMinutes} minutos
                    </Typography>
                  </Box>
                )}

                {content.typeId && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachFile fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Tipo: {content.typeId}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <SequenceControls
                canMoveUp={canMoveUp(content, allContents)}
                canMoveDown={canMoveDown(content, allContents)}
                onMoveUp={() => onMoveUp(content)}
                onMoveDown={() => onMoveDown(content)}
                disabled={disabled}
              />
              <Tooltip title="Editar">
                <IconButton
                  size="small"
                  onClick={() => onEdit(content)}
                  disabled={disabled}
                  color="primary"
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar">
                <IconButton
                  size="small"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={disabled}
                  color="error"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar el contenido &quot;{content.name}&quot;? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContentCard;



